import { api, ApiError } from '../../services/api';
import * as T from './types';

/** A refusal from the server: its message (in Portuguese) and its `ApiResult.errorCode`. */
export type PlannerError = Error & { code?: string };

function refused(result: any, fallback = 'Ocorreu um erro.'): PlannerError {
  const error: PlannerError = new Error(messageOf(result) ?? fallback);
  error.code = result?.errorCode ?? undefined;
  return error;
}

/** Every planner endpoint returns ApiResult<T>; this unwraps it and surfaces the server's message (in Portuguese). */
async function send<R>(method: 'get' | 'post' | 'put' | 'delete', url: string, body?: unknown): Promise<R> {
  try {
    const response = method === 'get' || method === 'delete'
      ? await api[method](url)
      : await api[method](url, body ?? null);
    const result = response.data;
    if (!result?.success) throw refused(result);
    return result.data as R;
  } catch (error: any) {
    if (error instanceof Error) throw error;
    const apiError = error as ApiError;
    if (!apiError.status) throw new Error('Não foi possível contactar o servidor.');
    throw refused(apiError.details, apiError.message ?? 'Ocorreu um erro.');
  }
}

function messageOf(result: any): string | undefined {
  if (!result || typeof result !== 'object') return undefined;
  if (Array.isArray(result.errors) && result.errors.length > 0) return result.errors.join('\n');
  return result.message || undefined;
}

const q = (value: string) => encodeURIComponent(value);

// ---------- Account ----------

export const getProfile = () => send<T.UserProfile>('get', '/api/user/profile');
export const updateProfile = (name: string | null) => send<T.UserProfile>('put', '/api/user/profile', { name });
/** The photo already reduced by the browser (JPEG as base64, at most 512 KB). */
export const setPhoto = (contentBase64: string) => send<T.UserProfile>('put', '/api/user/photo', { contentBase64 });
export const deletePhoto = () => send<T.UserProfile>('delete', '/api/user/photo');
/** The household account preselected when this user pays a trip; null clears it. Answers with the account chosen. */
export const getSettings = () => send<T.UserSettings>('get', '/api/user/settings');
export const updateSettings = (request: T.UpdateSettingsRequest) => send<T.UserSettings>('put', '/api/user/settings', request);
export const setShoppingAccount = (accountPublicId: string | null) =>
  send<string | null>('put', '/api/user/shopping-account', { accountPublicId });
/** The other sessions end; this one goes on with the token returned. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  send<T.ChangePasswordResult>('post', '/api/user/password', { currentPassword, newPassword });

/** The image behind a photoUrl, as an object URL (an <img> cannot send the token). Kept per URL, which changes with the photo. */
const photos = new Map<string, Promise<string | null>>();
export function getPhotoObjectUrl(photoUrl: string): Promise<string | null> {
  let photo = photos.get(photoUrl);
  if (!photo) {
    photo = api.get(photoUrl, { responseType: 'blob' })
      .then(response => URL.createObjectURL(response.data as Blob))
      .catch(() => { photos.delete(photoUrl); return null; });
    photos.set(photoUrl, photo);
  }
  return photo;
}

// ---------- Household ----------

/** The user's household, or null when they have none yet. */
export const getHousehold = () => send<T.Household | null>('get', '/api/household');
export const createHousehold = (body: T.CreateHouseholdRequest) => send<T.Household>('post', '/api/household', body);
export const updateHousehold = (body: T.UpdateHouseholdRequest) => send<T.Household>('put', '/api/household', body);
export const updateMealTimes = (body: T.MealTimesRequest) => send<T.Household>('put', '/api/household/meal-times', body);
export const addMember = (body: T.SaveHouseholdMemberRequest) => send<T.Household>('post', '/api/household/members', body);
export const updateMember = (id: T.Guid, body: T.SaveHouseholdMemberRequest) => send<T.Household>('put', `/api/household/members/${id}`, body);
export const removeMember = (id: T.Guid) => send<T.Household>('delete', `/api/household/members/${id}`);
export const cancelInvitation = (memberId: T.Guid) => send<T.Household>('delete', `/api/household/members/${memberId}/invitation`);
/** Pending invitations addressed to the current user. */
export const getInvitations = () => send<T.ReceivedInvitation[]>('get', '/api/household/invitations');
export const acceptInvitation = (id: T.Guid) => send<T.Household>('post', `/api/household/invitations/${id}/accept`);
export const declineInvitation = (id: T.Guid) => send<boolean>('post', `/api/household/invitations/${id}/decline`);

// ---------- Catalog ----------

export const getCatalogItems = (kind?: T.CatalogItemKind) =>
  send<T.CatalogItem[]>('get', kind ? `/api/catalog-items?kind=${kind}` : '/api/catalog-items');
export const getCatalogItem = (id: T.Guid) => send<T.CatalogItem>('get', `/api/catalog-items/${id}`);
export const createCatalogItem = (body: T.SaveCatalogItemRequest) => send<T.CatalogItem>('post', '/api/catalog-items', body);
export const updateCatalogItem = (id: T.Guid, body: T.SaveCatalogItemRequest) => send<T.CatalogItem>('put', `/api/catalog-items/${id}`, body);
export const deleteCatalogItem = (id: T.Guid) => send<boolean>('delete', `/api/catalog-items/${id}`);
/** Merges a duplicate item into the one that stays. */
export const mergeCatalogItem = (duplicateId: T.Guid, keepId: T.Guid) =>
  send<T.CatalogItem>('post', `/api/catalog-items/${duplicateId}/merge-into/${keepId}`);

// ---------- Recipes ----------

export const getRecipes = (frequency?: T.RecipeFrequency) =>
  send<T.RecipeSummary[]>('get', frequency ? `/api/recipes?frequency=${frequency}` : '/api/recipes');
export const getRecipe = (id: T.Guid) => send<T.Recipe>('get', `/api/recipes/${id}`);
export const createRecipe = (body: T.SaveRecipeRequest) => send<T.Recipe>('post', '/api/recipes', body);
export const updateRecipe = (id: T.Guid, body: T.SaveRecipeRequest) => send<T.Recipe>('put', `/api/recipes/${id}`, body);
export const deleteRecipe = (id: T.Guid) => send<boolean>('delete', `/api/recipes/${id}`);
/** "Desfazer" after deleting: the recipe was only hidden. */
export const restoreRecipe = (id: T.Guid) => send<boolean>('post', `/api/recipes/${id}/restore`);

// ---------- Meal plan ----------

/** The week containing `date`; the server creates it on first access. */
export const getWeekPlan = (date: T.DateOnly) => send<T.WeekPlan>('get', `/api/meal-plan/${date}`);
export const updateMealSlot = (slotId: T.Guid, body: T.SaveMealSlotRequest) => send<T.WeekPlan>('put', `/api/meal-plan/slots/${slotId}`, body);
/** "Efetivar": the meals still to settle (or only `slotIds`) and what they take from the stock. */
export const getMealSettlement = (slotIds?: T.Guid[]) =>
  send<T.MealSettlement>('get', `/api/meal-plan/settlement${slotIds ? `?${slotIds.map(id => `slot=${id}`).join('&')}` : ''}`);
/** Settles the meals (their ingredients leave the stock), or puts the quantities back with `undo`; returns the meals changed. */
export const settleMeals = (body: T.SettleMealsRequest) => send<T.SettledMeals>('post', '/api/meal-plan/settlement', body);
/** "Comeram o que estava planeado?" for a meal that has happened; the answer settles it. */
export const confirmMeal = (slotId: T.Guid, body: T.ConfirmMealRequest) => send<T.WeekPlan>('post', `/api/meal-plan/slots/${slotId}/confirm`, body);
export const getLeftoverSources = (slotId: T.Guid) => send<T.LeftoverSource[]>('get', `/api/meal-plan/slots/${slotId}/leftover-sources`);
/** Draft recipes for the week's empty meals; nothing is saved. */
export const suggestWeek = (date: T.DateOnly) => send<T.SuggestedMeal[]>('post', `/api/meal-plan/${date}/suggest`);
export const applySuggestions = (date: T.DateOnly, meals: T.ApplySuggestionRequest[]) => send<T.WeekPlan>('post', `/api/meal-plan/${date}/apply`, meals);
/** The usual side dish to suggest with a dish (the one made longest ago), or null. */
export const suggestSide = (recipeId: T.Guid) => send<T.RecipeSide | null>('get', `/api/meal-plan/side-suggestion/${recipeId}`);
export const getSlotSuggestions = (slotId: T.Guid, quickOnly: boolean, top = 8) =>
  send<T.RecipeSuggestion[]>('get', `/api/meal-plan/slots/${slotId}/suggestions?quick=${quickOnly}&top=${top}`);
export const getSuggestionSettings = () => send<T.MealSuggestionSettings>('get', '/api/meal-plan/suggestion-settings');
export const updateSuggestionSettings = (body: T.MealSuggestionSettings) =>
  send<T.MealSuggestionSettings>('put', '/api/meal-plan/suggestion-settings', body);
export const getMealPatterns = () => send<T.MealPattern[]>('get', '/api/meal-plan/patterns');
export const updateMealPatterns = (patterns: T.MealPattern[]) => send<T.MealPattern[]>('put', '/api/meal-plan/patterns', patterns);

// ---------- Freezer ----------

/** What is in the freezer (not finished, with portions left), oldest first. */
export const getFreezer = (includeFinished = false) => send<T.FrozenBatch[]>('get', `/api/freezer?includeFinished=${includeFinished}`);
export const createFrozenBatch = (body: T.SaveFrozenBatchRequest) => send<T.FrozenBatch>('post', '/api/freezer', body);
export const updateFrozenBatch = (id: T.Guid, body: T.SaveFrozenBatchRequest) => send<T.FrozenBatch>('put', `/api/freezer/${id}`, body);
export const finishFrozenBatch = (id: T.Guid, finished: boolean) => send<T.FrozenBatch>('post', `/api/freezer/${id}/finished?finished=${finished}`);

/** The "Sopa" row of the week containing `date`. */
export const getPotWeek = (date: T.DateOnly) => send<T.PotWeek>('get', `/api/pots/${date}`);
export const createPot = (body: T.SavePotRequest) => send<T.PotCooking>('post', '/api/pots', body);
export const updatePot = (id: T.Guid, body: T.SavePotRequest) => send<T.PotCooking>('put', `/api/pots/${id}`, body);
/** "Acabou a sopa" today, or still some left with `finished` false. */
export const finishPot = (id: T.Guid, finished: boolean) => send<T.PotCooking>('post', `/api/pots/${id}/finished?finished=${finished}`);
/** "Não fiz" (ingredients already taken go back), or made after all with `cancelled` false. */
export const cancelPot = (id: T.Guid, cancelled: boolean) => send<T.PotCooking>('post', `/api/pots/${id}/cancelled?cancelled=${cancelled}`);

// ---------- Tasks ----------

export const getTasks = () => send<T.HouseholdTask[]>('get', '/api/tasks');
export const getTask = (id: T.Guid) => send<T.HouseholdTask>('get', `/api/tasks/${id}`);
export const createTask = (body: T.SaveTaskRequest) => send<T.HouseholdTask>('post', '/api/tasks', body);
export const updateTask = (id: T.Guid, body: T.SaveTaskRequest) => send<T.HouseholdTask>('put', `/api/tasks/${id}`, body);
export const deleteTask = (id: T.Guid) => send<boolean>('delete', `/api/tasks/${id}`);
/** "Comprar X" task for a missing item. */
export const createNeedTask = (needId: T.Guid) => send<T.HouseholdTask>('post', `/api/tasks/from-need/${needId}`);
/** Occurrences between two dates plus everything overdue. */
export const getTaskAgenda = (from: T.DateOnly, to: T.DateOnly) => send<T.TaskAgenda>('get', `/api/tasks/agenda?from=${from}&to=${to}`);
export const completeOccurrence = (id: T.Guid, doneByPublicId?: T.Guid | null) =>
  send<T.TaskOccurrence>('post', `/api/tasks/occurrences/${id}/complete`, { doneByPublicId: doneByPublicId ?? null });
export const skipOccurrence = (id: T.Guid) => send<T.TaskOccurrence>('post', `/api/tasks/occurrences/${id}/skip`);
export const reopenOccurrence = (id: T.Guid) => send<T.TaskOccurrence>('post', `/api/tasks/occurrences/${id}/reopen`);
export const rescheduleOccurrence = (id: T.Guid, body: T.RescheduleOccurrenceRequest) =>
  send<T.TaskOccurrence>('put', `/api/tasks/occurrences/${id}`, body);
export const setChecklistItem = (occurrenceId: T.Guid, item: string, done: boolean) =>
  send<T.TaskOccurrence>('put', `/api/tasks/occurrences/${occurrenceId}/checklist`, { item, done });

// ---------- Shopping ----------

export const getShoppingNeeds = () => send<T.ShoppingNeed[]>('get', '/api/shopping/needs');
export const addShoppingNeed = (body: T.AddShoppingNeedRequest) => send<T.ShoppingNeed>('post', '/api/shopping/needs', body);
/** "Passo a prever?": makes the item a regular purchase or gives it a minimum at home. */
export const applyShoppingPrediction = (catalogItemPublicId: T.Guid, kind: T.ShoppingPredictionKind) =>
  send<boolean>('post', '/api/shopping/needs/predict', { catalogItemPublicId, kind });
/** "Não": the question does not come back for this item. */
export const dismissShoppingPrediction = (catalogItemPublicId: T.Guid) =>
  send<boolean>('post', '/api/shopping/needs/predict/dismiss', { catalogItemPublicId });
export const updateShoppingNeed = (id: T.Guid, body: T.UpdateShoppingNeedRequest) => send<T.ShoppingNeed>('put', `/api/shopping/needs/${id}`, body);
export const markShoppingNeedBought = (id: T.Guid) => send<T.ShoppingNeed>('post', `/api/shopping/needs/${id}/bought`);
export const cancelShoppingNeed = (id: T.Guid) => send<T.ShoppingNeed>('post', `/api/shopping/needs/${id}/cancel`);
export const reopenShoppingNeed = (id: T.Guid) => send<T.ShoppingNeed>('post', `/api/shopping/needs/${id}/reopen`);
export const assignShoppingNeedStore = (id: T.Guid, storePublicId: T.Guid | null) =>
  send<T.ShoppingNeed>('put', `/api/shopping/needs/${id}/store`, { storePublicId });
/** What generating the list from the week's meal plan would change; nothing is saved. */
export const previewMealPlanShopping = (date: T.DateOnly) =>
  send<T.MealPlanShoppingPreview>('get', `/api/shopping/needs/from-meal-plan/${date}`);
export const applyMealPlanShopping = (date: T.DateOnly, body: T.ApplyMealPlanShoppingRequest) =>
  send<T.ShoppingNeed[]>('post', `/api/shopping/needs/from-meal-plan/${date}`, body);

export const getRecurringItems = () => send<T.RecurringItem[]>('get', '/api/shopping/recurring');
/** What the list shows around its items in one light call: regular purchases due, the menu changed, the next trip. */
export const getShoppingStatus = (withMenuChange = true) =>
  send<T.ShoppingStatus>('get', `/api/shopping/status${withMenuChange ? '' : '?menu=false'}`);
export const createRecurringItem = (body: T.SaveRecurringItemRequest) => send<T.RecurringItem>('post', '/api/shopping/recurring', body);
export const updateRecurringItem = (id: T.Guid, body: T.SaveRecurringItemRequest) => send<T.RecurringItem>('put', `/api/shopping/recurring/${id}`, body);
export const deleteRecurringItem = (id: T.Guid) => send<boolean>('delete', `/api/shopping/recurring/${id}`);
/** Adds the chosen due items to the list and skips the others until next time. */
export const applyRecurringItems = (body: T.ApplyRecurringItemsRequest) => send<T.ShoppingNeed[]>('post', '/api/shopping/recurring/apply', body);

export const getStores = () => send<T.Store[]>('get', '/api/shopping/stores');
export const createStore = (body: T.SaveStoreRequest) => send<T.Store>('post', '/api/shopping/stores', body);
export const updateStore = (id: T.Guid, body: T.SaveStoreRequest) => send<T.Store>('put', `/api/shopping/stores/${id}`, body);
export const deleteStore = (id: T.Guid) => send<boolean>('delete', `/api/shopping/stores/${id}`);

export const getTrips = () => send<T.TripSummary[]>('get', '/api/shopping/trips');
export const getTrip = (id: T.Guid) => send<T.Trip>('get', `/api/shopping/trips/${id}`);
export const createTrip = (body: T.CreateTripRequest) => send<T.Trip>('post', '/api/shopping/trips', body);
/** Another day, or another store while nothing is in the cart. */
export const updateTrip = (id: T.Guid, body: T.UpdateTripRequest) => send<T.Trip>('put', `/api/shopping/trips/${id}`, body);
export const cancelTrip = (id: T.Guid) => send<T.Trip>('post', `/api/shopping/trips/${id}/cancel`);
/** One trip per store from the open list. */
export const splitTrips = (plannedDate: T.DateOnly) => send<T.SplitTripsResult>('post', '/api/shopping/trips/split', { plannedDate });
/** Store-mode changes (cart, unavailable, replace, ...), applied in order. */
export const syncTrip = (tripId: T.Guid, changes: T.TripChangeRequest[]) => send<T.TripSyncResult>('post', `/api/shopping/trips/${tripId}/sync`, changes);
export const payTrip = (tripId: T.Guid, body: T.PayTripRequest) => send<T.PayTripResult>('post', `/api/shopping/trips/${tripId}/pay`, body);
/** What a paid trip brought home, with the suggested place of each line. */
export const getPutAway = (tripId: T.Guid) => send<T.PutAway>('get', `/api/shopping/trips/${tripId}/put-away`);
export const putAway = (tripId: T.Guid, items: T.PutAwayLineRequest[]) =>
  send<T.PutAway>('post', `/api/shopping/trips/${tripId}/put-away`, { items });
/** "Desfazer": the lines a put-away returned in `undo` wait to be put away again, with the stock as it was. */
export const undoPutAway = (tripId: T.Guid, undo: T.PutAwayUndoLine[]) =>
  send<T.PutAway>('post', `/api/shopping/trips/${tripId}/put-away/undo`, undo);

// ---------- Pantry ----------

/** What is at home (meals that happened are taken out first). */
export const getStock = () => send<T.StockItem[]>('get', '/api/pantry');
export const saveStock = (body: T.SaveStockRequest) => send<T.StockItem>('put', '/api/pantry', body);
export const adjustStock = (itemId: T.Guid, delta: number) => send<T.StockItem>('post', `/api/pantry/${itemId}/adjust`, { delta });
/** "Recebi de…": what someone gave goes into the stock and off the list. */
export const receiveGift = (body: T.ReceiveGiftRequest) => send<T.ReceiveGiftResult>('post', '/api/pantry/gifts', body);
/** Who gave something before, the most recent first. */
export const getGiftGivers = () => send<string[]>('get', '/api/pantry/gifts/givers');
/** "Preparar a semana": the items whose stock is uncertain before the shopping. */
/** Applies what the rhythm learnt from the purchases suggests. */
export const applyRhythmSuggestion = (itemId: T.Guid, kind: T.RhythmSuggestionKind) =>
  send<T.StockItem>('post', `/api/pantry/${itemId}/rhythm`, { kind });
/** "Não": the rhythm stops suggesting anything for this item. */
export const dismissRhythmSuggestions = (itemId: T.Guid) => send<T.StockItem>('post', `/api/pantry/${itemId}/rhythm/dismiss`);
export const getPrepareWeek = () => send<T.PrepareWeek>('get', '/api/pantry/prepare-week');
/** Sets the stock by eye; "pouco" and "acabou" put the item on the list. */
export const setStockLevel = (itemId: T.Guid, level: T.StockLevel) => send<T.StockItem>('post', `/api/pantry/${itemId}/level`, { level });
export const removeStock = (itemId: T.Guid) => send<boolean>('delete', `/api/pantry/${itemId}`);
/** The bags made at home from an item (or the bag itself), with the numbers to suggest. */
export const getPreparations = (itemId: T.Guid, tripItemId?: T.Guid) =>
  send<T.Preparation[]>('get', `/api/pantry/${itemId}/preparations${tripItemId ? `?tripItem=${tripItemId}` : ''}`);
export const prepare = (bagId: T.Guid, body: T.PrepareRequest) => send<T.StockItem>('post', `/api/pantry/${bagId}/prepare`, body);
export const getStorageLocations = () => send<T.StorageLocation[]>('get', '/api/pantry/locations');
/** A place, or a zone inside `parentPublicId`. */
export const createStorageLocation = (name: string, parentPublicId?: T.Guid | null, isFreezer = false) =>
  send<T.StorageLocation>('post', '/api/pantry/locations', { name, parentPublicId: parentPublicId ?? null, isFreezer });
/** Marks a place as a freezer (where frozen food is kept) or not. */
export const setStorageLocationFreezer = (id: T.Guid, isFreezer: boolean) =>
  send<T.StorageLocation>('put', `/api/pantry/locations/${id}/freezer`, { isFreezer });
/** The icon and colour of a place in the house map; nulls go back to the ones its name suggests. */
export const setStorageLocationLook = (id: T.Guid, icon: string | null, color: string | null) =>
  send<T.StorageLocation>('put', `/api/pantry/locations/${id}/look`, { icon, color });
export const renameStorageLocation = (id: T.Guid, name: string) => send<T.StorageLocation>('put', `/api/pantry/locations/${id}`, { name });
/** The places (or the zones of one place) in this order. */
export const reorderStorageLocations = (ids: T.Guid[]) => send<boolean>('put', '/api/pantry/locations/order', ids);
/** Deleting a place also clears it from the items kept there. */
export const deleteStorageLocation = (id: T.Guid) => send<boolean>('delete', `/api/pantry/locations/${id}`);

// ---------- Calendar ----------

export const getCalendarEvent = (id: T.Guid) => send<T.CalendarEvent>('get', `/api/calendar/events/${id}`);
export const createCalendarEvent = (body: T.SaveCalendarEventRequest) => send<T.CalendarEvent>('post', '/api/calendar/events', body);
export const updateCalendarEvent = (id: T.Guid, body: T.SaveCalendarEventRequest) =>
  send<T.CalendarEvent>('put', `/api/calendar/events/${id}`, body);
export const deleteCalendarEvent = (id: T.Guid) => send<boolean>('delete', `/api/calendar/events/${id}`);
/** "De quem é" of any event; on a read one it is kept on the next reads. */
export const setCalendarEventMembers = (id: T.Guid, memberPublicIds: T.Guid[]) =>
  send<T.CalendarEvent>('put', `/api/calendar/events/${id}/members`, { memberPublicIds });
export const getCalendarFeeds = () => send<T.CalendarFeed[]>('get', '/api/calendar/feeds');
/** Reads the link at once: one that cannot be read is refused with the reason. */
export const createCalendarFeed = (body: T.SaveCalendarFeedRequest) => send<T.CalendarFeed>('post', '/api/calendar/feeds', body);
export const updateCalendarFeed = (id: T.Guid, body: T.SaveCalendarFeedRequest) => send<T.CalendarFeed>('put', `/api/calendar/feeds/${id}`, body);
export const deleteCalendarFeed = (id: T.Guid) => send<boolean>('delete', `/api/calendar/feeds/${id}`);
/** "Atualizar agora": the calendar with how the read went. */
export const syncCalendarFeed = (id: T.Guid) => send<T.CalendarFeed>('post', `/api/calendar/feeds/${id}/sync`);
/** The board for a tablet/TV: `days` (1 or 7) from `date`, one column per person plus "Casa". */
export const getCalendarBoard = (date: T.DateOnly, days: 1 | 7) =>
  send<T.CalendarBoard>('get', `/api/calendar/board?date=${date}&days=${days}`);

// ---------- Week ----------

export const getWeekOverview = (date: T.DateOnly) => send<T.WeekOverview>('get', `/api/week/${date}`);
/** Counts for "Para começar", without the lists. */
export const getWeekSetup = () => send<T.WeekSetup>('get', '/api/week/setup');
export const dismissWeekAlert = (alert: T.WeekAlert) =>
  send<boolean>('post', '/api/week/alerts/dismiss', { key: alert.key, causeHash: alert.causeHash });

// ---------- Assistant ----------

/** A spoken/typed command read into proposals; nothing is saved. */
export const interpret = (text: string) => send<T.InterpretResult>('post', '/api/assistant/interpret', { text });
export const confirmAssistant = (body: T.ConfirmActionsRequest) => send<T.AssistantActionResult[]>('post', '/api/assistant/confirm', body);
export const undoAssistant = (undos: Record<string, unknown>[]) => send<number>('post', '/api/assistant/undo', { undos });

// ---------- Expenses (paying a trip) ----------

export const getExpenseGroups = () => send<T.ExpenseGroupOption[]>('get', `/api/homeexpenses/groups?type=${q('Home')}`);
export const getExpenseAccounts = () => send<T.AccountOption[]>('get', `/api/homeexpenses/accounts?type=${q('Home')}`);
